import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import axiosInstance from '../../services/axiosInstance'
import Button from '../../components/common/Button'
import { Users as UsersIcon, ShieldCheck, UserMinus, UserPlus, Shield } from 'lucide-react'
import { toast } from 'react-toastify'
import { isSuperAdmin } from '../../utils/roleUtils'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import EditUserModal from '../../components/modals/EditUserModal'
import './styles.scss'

export default function Users() {
  const [users, setUsers] = useState([])
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user: currentUser } = useAuth()

  // Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [userToEdit, setUserToEdit] = useState(null)

  useEffect(() => {
    fetchUsers()
    fetchTeams()
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await axiosInstance.get('/api/users')
      setUsers(response.data)
      setError(null)
    } catch (err) {
      console.error('Error fetching users:', err)
      setError('You do not have permission to view this page or there was an error fetching data.')
    } finally {
      setLoading(false)
    }
  }

  const fetchTeams = async () => {
    try {
      const response = await axiosInstance.get('/api/users/teams')
      setTeams(response.data)
    } catch (err) {
      console.error('Error fetching teams:', err)
    }
  }

  const handleUpdateUser = async (userId, roles, teamId) => {
    try {
      await axiosInstance.put(`/api/users/${userId}`, null, {
        params: {
          roles: roles.join(','),
          teamId: teamId
        }
      })
      fetchUsers() // Refresh list
      toast.success('Institutional registry updated successfully.')
    } catch (err) {
      console.error('Error updating user:', err)
      throw err // Let the modal handle it
    }
  }

  const handleEditClick = (user) => {
    console.log('Triggering Edit Modal for user:', user?.username)
    setUserToEdit(user)
    setIsEditModalOpen(true)
  }

  const handleDeleteClick = (user) => {
    setUserToDelete(user)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!userToDelete) return
    setIsDeleting(true)
    try {
      await axiosInstance.delete(`/api/users/${userToDelete.id}`)
      setUsers(users.filter(u => u.id !== userToDelete.id))
      setIsDeleteModalOpen(false)
      toast.success(`User ${userToDelete.username} successfully removed from the system.`)
    } catch (err) {
      console.error('Error deleting user:', err)
      alert('Failed to delete user.')
    } finally {
      setIsDeleting(false)
      setUserToDelete(null)
    }
  }

  const stats = {
    total: users.length,
    admins: users.filter(u => isSuperAdmin(u)).length,
    academic: users.filter(u => u.roles.includes('ACADEMIC_ROLE')).length
  }

  if (loading) return (
    <div className="dashboard-container dashboard-loading">
      <div className="animate-pulse dashboard-loading-text">
        Retrieving University Personnel Registry...
      </div>
    </div>
  )

  return (
    <div className="dashboard-container">
      <header className="page-header-premium">
        <div className="hero-section">
          <UsersIcon className="header-icon-large" />
          <div className="title-stack">
            <div className="hero-badge">ADMINISTRATIVE ACCESS</div>
            <h1 className="dashboard-title">
              User Management <br />
              <span className="text-gradient">& Registry Control</span>
            </h1>
            <p className="subtitle">
              Audit regional personnel, assign institutional roles, and manage multi-tenant team access across the Memphis footprint.
            </p>
          </div>
        </div>
      </header>

      <section className="stats-bar">
        <div className="stat-item">
          <div className="stat-container-stack">
            <span className="stat-label">Total Registry Nodes</span>
            <span className="stat-value">{stats.total}</span>
            <div className="stat-subtext-meta">Registered Personnel</div>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-container-stack">
            <span className="stat-label">Super Administrators</span>
            <span className="stat-value">{stats.admins}</span>
            <div className="stat-subtext-meta">Elevated Access Nodes</div>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-container-stack">
            <span className="stat-label">Academic Hub Members</span>
            <span className="stat-value">{stats.academic}</span>
            <div className="stat-subtext-meta">Instructional Personnel</div>
          </div>
        </div>
      </section>

      {error ? (
        <div className="users-error-message">{error}</div>
      ) : (
        <div className="analytics-card card--full-width animate-fade-in">
          <div className="card-header">
            <h3 className="card-title">Personnel Registry Audit</h3>
            <p className="card-subtitle">Detailed audit log of all institutional accounts and their permission vectors.</p>
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Contact Information</th>
                  <th>Full Name</th>
                  <th>Institutional Roles</th>
                  <th>Org / Team Mapping</th>
                  <th className="text-right">Action Hub</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr 
                    key={u.id} 
                    className="interactive-row"
                    onClick={() => handleEditClick(u)}
                  >
                    <td className="font-medium">{u.username}</td>
                    <td>
                      <div className="email-cell">{u.email}</div>
                    </td>
                    <td>{u.fullName}</td>
                    <td>
                      <div className="role-badges">
                        {u.roles
                          .filter(role => !['USER', 'gateway_admin_realm', 'gateway_admin'].includes(role))
                          .map(role => (
                            <span key={role} className={`role-badge role-${role.toLowerCase()}`}>
                              {role}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td>
                      <div className="mapping-cell">
                        <span className="org-name">{u.organizationName || 'No Organization'}</span>
                        <span className="team-name">{u.teamName || 'No Team Assignment'}</span>
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="user-actions">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(u);
                          }}
                        >
                          <ShieldCheck size={14} className="mr-s" />
                          Roles
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(u);
                          }}
                          disabled={u.username === currentUser?.username}
                        >
                          <UserMinus size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={userToEdit}
        teams={teams}
        onSave={handleUpdateUser}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete User Registry?"
        message={`This will permanently remove ${userToDelete?.username}'s account from the university system. This action cannot be undone.`}
        confirmText="Remove Account"
        type="danger"
      />
    </div>
  )
}
